/**
 * Tournament Service
 *
 * Provides React hooks and callable wrappers for tournament features.
 * All data is sourced from live Firestore.
 */

import { useState, useEffect } from 'react';
import {
    collection, query, where, orderBy, limit,
    onSnapshot, doc, getDocs
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import type { Tournament, Squad } from '../types';

// ─── Hook: useMySquads ──────────────────────────────────────────────────────

/**
 * Real-time listener for the current player's squads.
 * Mock mode: returns mySquads from mockData.
 * Live mode: queries Firestore squads where memberUids contains uid.
 */
export function useMySquads(uid: string | undefined) {
    const [squads, setSquads] = useState<Squad[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!uid) { setLoading(false); return; }

        const squadsQuery = query(
            collection(db, 'squads'),
            where('memberUids', 'array-contains', uid),
            limit(20),
        );

        const unsubscribe = onSnapshot(squadsQuery, (snapshot) => {
            const results: Squad[] = snapshot.docs.map((d) => {
                const data = d.data();
                return {
                    id: d.id,
                    name: data.name || '',
                    game: data.game || '',
                    tag: data.tag || '',
                    status: data.status || 'forming',
                    leaderUid: data.leaderUid || '',
                    memberUids: data.memberUids || [],
                    members: data.members || [],
                    color: data.color || '',
                    region: data.region || '',
                    rank: data.rank || '',
                } as Squad;
            });
            setSquads(results);
            setLoading(false);
        }, () => {
            setLoading(false);
        });

        return () => unsubscribe();
    }, [uid]);

    return { squads, loading };
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface LiveMyTournament {
    id: string;
    game: string;
    title: string;
    status: 'upcoming' | 'live' | 'completed' | 'registering' | 'registration closed';
    entryFee: number;
    startTime: string;
    placement?: number;
    kills?: number;
    reward?: number;
    roomId?: string;
}

export interface LeaderboardEntry {
    rank: number;
    username: string;
    game: string;
    tournaments: number;
    winnings: number;
    photoURL?: string;
    playerId?: string;
}

// ─── Hook: useMyTournaments ─────────────────────────────────────────────────

export function useMyTournaments(uid: string | undefined) {
    const [tournaments, setTournaments] = useState<LiveMyTournament[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!uid) { setLoading(false); return; }

        // Listen to user's active entries
        const entriesQuery = query(
            collection(db, 'entries'),
            where('userId', '==', uid),
            orderBy('joinedAt', 'desc'),
            limit(20),
        );

        const unsubscribe = onSnapshot(entriesQuery, async (snapshot) => {
            if (snapshot.empty) {
                setTournaments([]);
                setLoading(false);
                return;
            }

            const results: LiveMyTournament[] = [];

            for (const entryDoc of snapshot.docs) {
                const entryData = entryDoc.data();
                if (entryData.status === 'refunded') continue; // skip left tournaments

                const tourneyId = entryData.tournamentId;
                
                // If the entry has denormalized fields (new structure), use them directly! NO EXTRA READS!
                if (entryData.tournamentTitle && entryData.game) {
                    results.push({
                        id: tourneyId,
                        game: entryData.game,
                        title: entryData.tournamentTitle,
                        // Legacy entries don't have tournament status denormalized, so we assume upcoming/live based on time if we wanted to
                        // For simplicity in the UI, we can just say 'upcoming' or use the entry status
                        status: 'upcoming', 
                        entryFee: entryData.entryFee || 0,
                        startTime: entryData.startTime || '',
                        placement: entryData.placement,
                        kills: entryData.kills,
                        reward: entryData.reward,
                        roomId: entryData.roomId,
                    });
                    continue;
                }

                // Fallback for legacy entries created before denormalization
                try {
                    const { getDoc } = await import('firebase/firestore');
                    const tourneySnap = await getDoc(doc(db, `tournaments/${tourneyId}`));
                    if (tourneySnap.exists()) {
                        const t = tourneySnap.data();
                        results.push({
                            id: tourneyId,
                            game: t.game || '',
                            title: t.title || '',
                            status: (t.status as any) || 'upcoming',
                            entryFee: t.entryFee || 0,
                            startTime: t.startTime || '',
                            placement: entryData.placement,
                            kills: entryData.kills,
                            reward: entryData.reward,
                            roomId: t.roomId,
                        });
                    }
                } catch (err) {
                    console.error(`Failed to fetch tournament ${tourneyId}:`, err);
                }
            }

            setTournaments(results);
            setLoading(false);
        }, (err) => {
            console.error('Entries listener error:', err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [uid]);

    return { tournaments, loading };
}

// ─── Hook: useLeaderboard ───────────────────────────────────────────────────

export function useLeaderboard() {
    const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {

        const leaderboardQuery = query(
            collection(db, 'users'),
            orderBy('totalWinnings', 'desc'),
            limit(20),
        );

        const unsubscribe = onSnapshot(leaderboardQuery, (snapshot) => {
            const results: LeaderboardEntry[] = snapshot.docs
                .map((d) => {
                    const data = d.data();
                    return {
                        rank: 0, // will be set after filtering
                        username: data.username || 'Unknown',
                        game: data.primaryGame || 'PUBG',
                        tournaments: data.tournamentsPlayed || 0,
                        winnings: data.totalWinnings || 0,
                        photoURL: data.photoURL || '',
                        playerId: data.playerId || '',
                    };
                })
                .filter(e => e.tournaments > 0 && e.winnings > 0)
                .map((e, idx) => ({ ...e, rank: idx + 1 }));
            setEntries(results);
            setLoading(false);
        }, (err) => {
            console.error('Leaderboard listener error:', err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    return { entries, loading };
}

// ─── Hook: useHasJoined ─────────────────────────────────────────────────────

/**
 * Real-time check for whether the current user has joined a tournament.
 * Uses the deterministic entry ID pattern: `{uid}_{tournamentId}`
 */
export function useHasJoined(uid: string | undefined, tournamentId: string | undefined) {
    const [hasJoined, setHasJoined] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!uid || !tournamentId) {
            setLoading(false);
            return;
        }

        const entryId = `${uid}_${tournamentId}`;
        const entryRef = doc(db, `entries/${entryId}`);

        const unsubscribe = onSnapshot(entryRef, (snap) => {
            // Joined if document exists and status is not "refunded"
            setHasJoined(snap.exists() && snap.data()?.status !== 'refunded');
            setLoading(false);
        }, (err) => {
            console.error('Entry check error:', err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [uid, tournamentId]);

    return { hasJoined, loading };
}

// ─── Hook: useTournament ────────────────────────────────────────────────────

/**
 * Real-time listener for a single tournament by ID.
 */
export function useTournament(tournamentId: string | undefined) {
    const [tournament, setTournament] = useState<Tournament | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!tournamentId) { setLoading(false); return; }

        const tourneyRef = doc(db, `tournaments/${tournamentId}`);

        const unsubscribe = onSnapshot(tourneyRef, (snap) => {
            if (snap.exists()) {
                setTournament({ id: snap.id, ...snap.data() } as Tournament);
            } else {
                setTournament(null);
            }
            setLoading(false);
        }, (err) => {
            console.error('Tournament listener error:', err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [tournamentId]);

    return { tournament, loading };
}

// ─── Cloud Function Callers ─────────────────────────────────────────────────

interface JoinPayload {
    tournamentId: string;
    squadId?: string;
}

interface LeavePayload {
    tournamentId: string;
}

interface CloudFnResult {
    success: boolean;
    message: string;
}

export async function callJoinTournament(tournamentId: string, squadId?: string): Promise<CloudFnResult> {
    const fn = httpsCallable<JoinPayload, CloudFnResult>(functions, 'joinTournament');
    const result = await fn({ tournamentId, squadId });
    return result.data;
}

export async function callLeaveTournament(tournamentId: string): Promise<CloudFnResult> {
    const fn = httpsCallable<LeavePayload, CloudFnResult>(functions, 'leaveTournament');
    const result = await fn({ tournamentId });
    return result.data;
}

// ─── Types: Tournament Entry ────────────────────────────────────────────────

export interface TournamentEntry {
    id: string;
    userId: string;
    tournamentId: string;
    username: string;
    ign: string;
    photoURL: string;
    squadId: string | null;
    squadName: string;
    status: string;
}

// ─── Hook: useTournamentEntries ─────────────────────────────────────────────

/**
 * Real-time subscription to all active entries for a tournament.
 * Returns denormalized player data (username, ign, photoURL, squadName)
 * so the UI can render roster lists without sub-reads.
 */
export function useTournamentEntries(tournamentId: string | undefined) {
    const [entries, setEntries] = useState<TournamentEntry[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!tournamentId) {
            setLoading(false);
            return;
        }

        const entriesQuery = query(
            collection(db, 'entries'),
            where('tournamentId', '==', tournamentId),
            where('status', '==', 'active'),
            orderBy('joinedAt', 'asc'),
        );

        const unsubscribe = onSnapshot(entriesQuery, (snapshot) => {
            const results: TournamentEntry[] = snapshot.docs.map((d) => {
                const data = d.data();
                return {
                    id: d.id,
                    userId: data.userId || '',
                    tournamentId: data.tournamentId || '',
                    username: data.username || 'Unknown',
                    ign: data.ign || '',
                    photoURL: data.photoURL || '',
                    squadId: data.squadId || null,
                    squadName: data.squadName || '',
                    status: data.status || 'active',
                };
            });
            setEntries(results);
            setLoading(false);
        }, (err) => {
            console.error('Tournament entries listener error:', err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [tournamentId]);

    return { entries, loading };
}

// ─── Hook: useFeaturedTournaments ──────────────────────────────────────────

export function useFeaturedTournaments() {
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {

        const q = query(
            collection(db, 'tournaments'),
            where('status', 'in', ['open', 'registering', 'registration closed', 'upcoming', 'live']),
            orderBy('startTime', 'asc'),
            limit(3)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const results: Tournament[] = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as Tournament));
            setTournaments(results);
            setLoading(false);
        }, (err) => {
            console.error('Featured tournaments listener error:', err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    return { tournaments, loading };
}

// ─── Hook: useTournamentGameCounts ─────────────────────────────────────────

export function useTournamentGameCounts() {
    const [counts, setCounts] = useState({ pubg: 0, freeFire: 0, mlbb: 0, wildRift: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {

        async function fetchCounts() {
            try {
                const q = query(
                    collection(db, 'tournaments'),
                    where('status', 'in', ['open', 'registering', 'registration closed', 'upcoming', 'live']),
                    limit(100)
                );
                const snapshot = await getDocs(q);
                
                setCounts({
                    pubg: snapshot.docs.filter(d => d.data().game === 'PUBG').length,
                    freeFire: snapshot.docs.filter(d => d.data().game === 'Free Fire').length,
                    mlbb: snapshot.docs.filter(d => d.data().game === 'MLBB').length,
                    wildRift: snapshot.docs.filter(d => d.data().game === 'Wild Rift').length,
                });
            } catch (err) {
                console.error('Game counts error:', err);
            } finally {
                setLoading(false);
            }
        }
        
        fetchCounts();
    }, []);

    return { counts, loading };
}

